import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { CategoriesProps } from "../../types/category";
import {
  ItemDateBy,
  ItemSizeOptionProps,
  ItemSortBy,
  ItemSortOrder,
  ItemsProps,
} from "../../types/item";
import { MaterialsProps } from "../../types/material";

export interface ItemsState {
  items: ItemsProps[];
  totalItems: number;
  currentItem: ItemsProps | null;
  currentItemLoading: boolean;
  categoryOptions: CategoriesProps[];
  materialOptions: MaterialsProps[];
  sizeOptions: ItemSizeOptionProps[];
  loading: boolean;
  error: string | null;
}

const initialState: ItemsState = {
  items: [],
  totalItems: 0,
  currentItem: null,
  currentItemLoading: false,
  categoryOptions: [],
  materialOptions: [],
  sizeOptions: [],
  loading: false,
  error: null,
};

type FetchItemsListDataProps = {
  page: number;
  size?: number;
  fromDate?: string | null;
  toDate?: string | null;
  search?: string | null;
  // Code range, both ends included; either can be left out.
  codeFrom?: string | null;
  codeTo?: string | null;
  main?: boolean;
  date_by?: ItemDateBy;
  sort_by?: ItemSortBy;
  sort_order?: ItemSortOrder;
};

export const fetchItemsListData = createAsyncThunk(
  "fetchItemsListData",
  async (req: FetchItemsListDataProps) => {
    let response: any;
    await GET(`/v1/admin/items`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchItemDetail = createAsyncThunk(
  "fetchItemDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/items/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchItemCategoriesData = createAsyncThunk(
  "fetchItemCategoriesData",
  async () => {
    let response: any;
    await GET(`/v1/admin/categories`, { all: true })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchItemMaterialsData = createAsyncThunk(
  "fetchItemMaterialsData",
  async () => {
    let response: any;
    await GET(`/v1/admin/materials`, { all: true })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The sizes items already have, offered on the item form.
export const fetchItemSizesData = createAsyncThunk(
  "fetchItemSizesData",
  async () => {
    let response: any;
    await GET(`/v1/admin/items/sizes`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const itemsSlice = createSlice({
  name: "items",
  initialState,
  reducers: {
    clearCurrentItem: (state) => {
      state.currentItem = null;
      state.currentItemLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchItemsListData.pending, (state, action: any) => {
      state.items = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchItemsListData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload.data.status === "success") {
        state.items = action.payload.data.data.records;
        state.totalItems = action.payload.data.data.total_records;
        state.error = null;
      } else {
        state.items = [];
        state.error =
          action?.payload?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
      }
    });
    builder.addCase(fetchItemsListData.rejected, (state, action: any) => {
      state.items = [];
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
    });
    builder.addCase(fetchItemDetail.pending, (state) => {
      state.currentItemLoading = true;
      state.currentItem = null;
    });
    builder.addCase(fetchItemDetail.fulfilled, (state, action: any) => {
      state.currentItemLoading = false;
      if (action.payload?.data?.status === "success") {
        state.currentItem = action.payload.data.data;
      }
    });
    builder.addCase(fetchItemDetail.rejected, (state) => {
      state.currentItemLoading = false;
      state.currentItem = null;
    });
    // Form options keep their previous values while reloading, so open selects don't flash empty.
    builder.addCase(fetchItemCategoriesData.fulfilled, (state, action: any) => {
      state.categoryOptions =
        action.payload?.data?.status === "success"
          ? action.payload.data.data.records
          : [];
    });
    builder.addCase(fetchItemMaterialsData.fulfilled, (state, action: any) => {
      state.materialOptions =
        action.payload?.data?.status === "success"
          ? action.payload.data.data.records
          : [];
    });
    builder.addCase(fetchItemSizesData.fulfilled, (state, action: any) => {
      state.sizeOptions =
        action.payload?.data?.status === "success"
          ? action.payload.data.data.records
          : [];
    });
  },
});

export const { clearCurrentItem } = itemsSlice.actions;

export const getItems = (state: RootState) => state.items.items;

export const getTotalItems = (state: RootState) => state.items.totalItems;

export const getCurrentItem = (state: RootState) => state.items.currentItem;

export const getCurrentItemLoading = (state: RootState) =>
  state.items.currentItemLoading;

export const getItemCategoryOptions = (state: RootState) =>
  state.items.categoryOptions;

export const getItemMaterialOptions = (state: RootState) =>
  state.items.materialOptions;

export const getItemSizeOptions = (state: RootState) => state.items.sizeOptions;

export default itemsSlice.reducer;
